/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './PreferencesSecurity.scss';

import { bool } from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';

import { NON_BREAKING_SPACE } from 'services/Formats';
import If from 'components/shared/If/If';
import { ReactComponent as ChevronRight } from 'assets/icons/chevron-right.svg';
import { ReactComponent as LockIcon } from 'assets/icons/lock.svg';
import PreferencesCol from 'components/layouts/Preferences/shared/PreferencesCol';
import PreferencesSectionHeader from 'components/layouts/Preferences/shared/PreferencesSectionHeader';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesRowLabel from 'components/layouts/Preferences/shared/PreferencesRowLabel';
import { hasGrantedViewAccess } from 'state/preferences/PreferencesSelectors';
import {isManagePassKeyEnabled} from 'services/FeatureFlags';
import {
  preferencesLinks,
  passwordUrl,
  usernameUrl,
  productLinkBuilder
} from 'services/Externals';
import {
  canViewGrantViewAccess,
  canViewAlertsAndNotifications,
  getView,
  getProductClassFromView
} from 'state/view/ViewSelectors';
import { multiFactorSecurityLinkEnabled, retailTracEnabled } from 'services/FeatureFlags';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import { getHasRetailTracAccountOnly, getHasOwnedCollegeSavingsOnly } from 'state/account/AccountSelectors';


const authenticationMethodLink = preferencesLinks.multiFactorAuthentication;

const authenticationMethodText = multiFactorSecurityLinkEnabled()
  ? 'Multi-Factor Authentication'
  : 'Two Factor Authentication';

const UpdateUserNameLink = ({ isMobile, productClass }) => {
  return (
    <a
      href={usernameUrl(productClass)}
      className={isMobile ? 'preferences-security-link' : ''}
      id="e2e-profile-username-link">
      {isMobile ? (
        <Fragment>
          <div>Change User Name</div>
          <ChevronRight className="preferences-chevron-right" />
        </Fragment>
      ) : (
        'User Name'
      )}
    </a>
  );
};

const UpdatePasswordLink = ({ isMobile, productClass }) => {
  return (
    <a
      href={passwordUrl(productClass)}
      className={isMobile ? 'preferences-security-link' : ''}
      id="e2e-profile-password-link">
      {isMobile ? (
        <Fragment>
          <div>Change Password</div>
          <ChevronRight className="preferences-chevron-right" />
        </Fragment>
      ) : (
        'Password'
      )}
    </a>
  );
};

const UserNameLinkWithPermissions = WithUserPermissions(UpdateUserNameLink);
const PasswordLinkWithPermissions = WithUserPermissions(UpdatePasswordLink);

const DesktopSecurity = ({
  canViewAlertsAndNotifications,
  showGVA,
  productClass,
  hasRetailTracAccountOnly,
  hasOwnedCollegeSavingsOnly
}) => (
  <Fragment>
    <PreferencesRow>
      <PreferencesCol col={3}>
        <PreferencesRowLabel label="Login" />
      </PreferencesCol>
      <PreferencesCol col={6}>
        <div className="preferences-security-text">
          Update your Login information:
        </div>
        <div className="login-links">
          <UserNameLinkWithPermissions
            shouldDisable
            styleOverride={{ display: 'block' }}
            productClass={productClass}
          />
          <PasswordLinkWithPermissions
            shouldDisable
            productClass={productClass}
            styleOverride={{ display: 'block' }}
          />
          <a
            href={productLinkBuilder(
              preferencesLinks.securityQuestions,
              productClass
            )}
            id="e2e-profile-security-questions-link">
            Security Questions
          </a>
          <a
            href={productLinkBuilder(authenticationMethodLink, productClass)}
            id="e2e-profile-multi-factor-link">
            {authenticationMethodText}
          </a>
          <div className="account-protection-disclaimer">
            Learn how our{NON_BREAKING_SPACE}
            <a
              className="preferences-security-link account-protection-link"
              href={preferencesLinks.accountProtectionProgram}
              id="e2e-profile-account-protection-link">
              Account Protection Program
            </a>
            {NON_BREAKING_SPACE}keeps you covered in the event of fraud.
          </div>
          {isManagePassKeyEnabled() && !hasOwnedCollegeSavingsOnly && (
            <a
              href={preferencesLinks.managePassKey}
              id="profile-manage-passkey-link"
            >
              Manage Passkey
            </a>
          )}
        </div>
      </PreferencesCol>

      <PreferencesCol col={3} />
    </PreferencesRow>
    <If test={retailTracEnabled()? !hasRetailTracAccountOnly : true}>
    {showGVA && (
      <PreferencesRow>
        <PreferencesCol col={3}>
          <PreferencesRowLabel label="Grant View Access" />
        </PreferencesCol>

        <PreferencesCol col={6}>
          <div className="preferences-security-text">
            Allow other T. Rowe Price clients to view your account for fast,
            efficient information sharing.
          </div>
        </PreferencesCol>

        <PreferencesCol col={3}>
          <Link
            className="preferences-grant-view-acess"
            id="e2e-profile-grant-view-link"
            to="/profile/grant-view-access">
            <span className="preferences-security-link">
              Add/Update View Access
            </span>
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}
    {canViewAlertsAndNotifications && (
      <PreferencesRow>
        <PreferencesCol col={3}>
          <PreferencesRowLabel label="Alerts &amp; Notifications" />
        </PreferencesCol>

        <PreferencesCol col={6}>
          <div className="preferences-security-text ">
            You can choose to have your electronic alerts delivered via text
            messaging in addition to email when certain updates are made online.
          </div>
        </PreferencesCol>

        <PreferencesCol col={3}>
          <Link
            className="preferences-alerts-and-notifications-link"
            to="/profile/alerts-and-notifications">
            <span className="preferences-security-link">
              Edit Alerts &amp; Notifications
            </span>
          </Link>
        </PreferencesCol>
      </PreferencesRow>
    )}
    </If>
  </Fragment>
);

DesktopSecurity.propTypes = {
  canViewAlertsAndNotifications: bool.isRequired
};

const MobileSecurity = ({
  canViewAlertsAndNotifications,
  showGVA,
  productClass,
  hasRetailTracAccountOnly,
  hasOwnedCollegeSavingsOnly
}) => (
  <Fragment>
    <PreferencesRow>
      <PreferencesCol col={12}>
        <UserNameLinkWithPermissions
          shouldDisable
          isMobile
          productClass={productClass}
        />
      </PreferencesCol>
    </PreferencesRow>
    <PreferencesRow>
      <PreferencesCol col={12}>
        <PasswordLinkWithPermissions
          shouldDisable
          productClass={productClass}
          isMobile
        />
      </PreferencesCol>
    </PreferencesRow>
    <PreferencesRow>
      <PreferencesCol col={12}>
        <a
          className="preferences-security-link"
          href={productLinkBuilder(
            preferencesLinks.securityQuestions,
            productClass
          )}>
          <div>Change Security Questions</div>
          <ChevronRight className="preferences-chevron-right" />
        </a>
      </PreferencesCol>
    </PreferencesRow>
    <PreferencesRow>
      <PreferencesCol col={12}>
        <a
          href={productLinkBuilder(
            preferencesLinks.multiFactorAuthentication,
            productClass
          )}
          className="preferences-security-link">
          Change Multi-Factor Authentication
          <ChevronRight className="preferences-chevron-right" />
        </a>
      </PreferencesCol>
    </PreferencesRow>
    {isManagePassKeyEnabled() && !hasOwnedCollegeSavingsOnly &&
    (<PreferencesRow>
      <PreferencesCol col={12}>
        <a
          href={
            preferencesLinks.managePassKey}
          className="preferences-security-link">
          Manage Passkey
          <ChevronRight className="preferences-chevron-right" />
        </a>
      </PreferencesCol>
    </PreferencesRow>)}
    <If test={retailTracEnabled()? !hasRetailTracAccountOnly : true}>
      {showGVA && (
          <PreferencesRow>
            <PreferencesCol col={12}>
              <Link
                  className="preferences-security-link"
                  id="e2e-profile-grant-view-link"
                  to="/profile/grant-view-access">
                <div>Add/Update View Access</div>
                <ChevronRight className="preferences-chevron-right"/>
              </Link>
            </PreferencesCol>
          </PreferencesRow>
      )}
      {canViewAlertsAndNotifications && (
          <PreferencesRow>
            <PreferencesCol col={12}>
              <Link
                  className="preferences-security-link"
                  to="/profile/alerts-and-notifications">
                <div>Alerts &amp; Notifications</div>
                <ChevronRight className="preferences-chevron-right"/>
              </Link>
            </PreferencesCol>
          </PreferencesRow>
      )}
    </If>
  </Fragment>
);

MobileSecurity.propTypes = {
  canViewAlertsAndNotifications: bool.isRequired
};

const PreferencesSecurity = ({
  canViewAlertsAndNotifications,
  isDesktopLayout,
  viewAccess,
  canViewGrantViewAccess,
  view,
  productClass,
  hasRetailTracAccountOnly,
  hasOwnedCollegeSavingsOnly
}) => {
  return (
    <div className="preferences-security">
      <PreferencesSectionHeader Icon={LockIcon} text="Security Preferences" />
      {isDesktopLayout ? (
        <DesktopSecurity
          canViewAlertsAndNotifications={canViewAlertsAndNotifications}
          viewAccess={viewAccess}
          showGVA={canViewGrantViewAccess}
          view={view}
          productClass={productClass}
          hasRetailTracAccountOnly={hasRetailTracAccountOnly}
          hasOwnedCollegeSavingsOnly={hasOwnedCollegeSavingsOnly}
        />
      ) : (
        <MobileSecurity
          canViewAlertsAndNotifications={canViewAlertsAndNotifications}
          viewAccess={viewAccess}
          showGVA={canViewGrantViewAccess}
          view={view}
          productClass={productClass}
          hasRetailTracAccountOnly={hasRetailTracAccountOnly}
          hasOwnedCollegeSavingsOnly={hasOwnedCollegeSavingsOnly}
        />
      )}
    </div>
  );
};

PreferencesSecurity.propTypes = {
  canViewAlertsAndNotifications: bool.isRequired,
  hasOwnedCollegeSavingsOnly: bool.isRequired
};

const mapStateToProps = (state) => ({
  canViewAlertsAndNotifications: canViewAlertsAndNotifications(state),
  viewAccess: hasGrantedViewAccess(state),
  isDesktopLayout: state.view.isDesktopLayout,
  canViewGrantViewAccess: canViewGrantViewAccess(state),
  view: getView(state),
  productClass: getProductClassFromView(state),
  hasRetailTracAccountOnly: getHasRetailTracAccountOnly(state),
  hasOwnedCollegeSavingsOnly: getHasOwnedCollegeSavingsOnly(state)
});

export default connect(mapStateToProps)(PreferencesSecurity);
